/**
 * SRES · Step 6 · Estimation Settings (Iter82 · Feb 2026).
 *
 * Replaces the StepPlaceholder for `estimation_params`. Shows the
 * current modelling configuration alongside two paths forward:
 *
 *   1. **Recommended Defaults** — a one-click quick-link that PATCHes
 *      sensible values (block sizes, search radii, sample counts,
 *      cutoff grade) via the existing `/api/projects/{pid}/configuration`
 *      endpoint, then jumps to Step 7.
 *
 *   2. **Advanced Configuration** — takes the user into the
 *      per-parameter wizard sub-steps (density, statistics, composite,
 *      domain, variogram) via the Project Configuration workspace.
 *
 * No backend endpoint changes — everything reuses the existing
 * project-configuration patch route.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Gear, Sparkle, ArrowRight, PlayCircle, CheckCircle, Lightning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Feb 2026 · defaults tuned for a small-tonnage gold project (~15 Mt).
// If per-commodity presets appear later, we branch on project.commodity.
const RECOMMENDED_DEFAULTS = {
  "modelling.block_size_x":           { value: 10.0,  unit: "m" },
  "modelling.block_size_y":           { value: 10.0,  unit: "m" },
  "modelling.block_size_z":           { value: 5.0,   unit: "m" },
  "modelling.composite_length":       { value: 1.0,   unit: "m" },
  "modelling.density_default":        { value: 2.75,  unit: "t/m3" },
  "modelling.cutoff_grade":           { value: 0.5,   unit: "g/t" },
  "modelling.search_radius_x":        { value: 50.0,  unit: "m" },
  "modelling.search_radius_y":        { value: 50.0,  unit: "m" },
  "modelling.search_radius_z":        { value: 25.0,  unit: "m" },
  "modelling.min_samples_per_block":  { value: 4,     unit: null },
  "modelling.max_samples_per_block":  { value: 12,    unit: null },
  "modelling.estimation_method":      { value: "OK",  unit: null,
                                            display_hint: "Ordinary Kriging" },
};

const PRESET_ROWS = [
  ["Block size (X × Y × Z)",  "10 × 10 × 5 m"],
  ["Composite length",         "1.0 m"],
  ["Default density",          "2.75 t/m³"],
  ["Reporting cut-off",         "0.5 g/t"],
  ["Search radius (X, Y, Z)", "50, 50, 25 m"],
  ["Samples / block (min · max)", "4 · 12"],
  ["Estimation method",       "Ordinary Kriging (OK)"],
];

export default function EstimationSettingsStep({ projectId }) {
  const { pid: pidFromRoute } = useParams();
  const pid = projectId || pidFromRoute;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [applied, setApplied] = useState(false);

  const cfgQ = useQuery({
    queryKey: ["project-config", pid],
    queryFn:  () => api.get(`/projects/${pid}/configuration`).then((r) => r.data),
    enabled:  !!pid,
  });
  const modelling = cfgQ.data?.configuration?.modelling || {};
  const hasCurrent = !!modelling.block_size_x?.value;

  const applyMut = useMutation({
    mutationFn: () => api.patch(
      `/projects/${pid}/configuration`,
      {
        patches: Object.fromEntries(
          Object.entries(RECOMMENDED_DEFAULTS).map(([k, v]) => [k, {
            value:            v.value,
            source:            "recommended_default",
            source_label:     "SRES · Recommended Defaults preset",
            confidence:        0.85,
            unit:              v.unit,
            review_status:    "AI Suggested",
          }]),
        ),
        summary: "Applied SRES Recommended Defaults preset (Iter82 Step 6 quick-link)",
      },
    ).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-config", pid] });
      setApplied(true);
      toast.success("Recommended defaults applied — moving to Step 7…");
      // Small delay so the user sees the checkmark, then jump to Run Estimation.
      setTimeout(() => navigate(`/app/projects/${pid}/wizard/block_model`), 800);
    },
    onError: (e) => toast.error(e?.response?.data?.detail
                                     || "Failed to apply defaults"),
  });

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto"
              data-testid="estimation-settings-step">
      <header className="border-b border-slate-200 pb-4">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
          SRES · Step 6 of 9 · Estimation Settings
        </div>
        <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 mt-1">
          Configure the block-model parameters
        </h1>
        <p className="text-[12.5px] text-slate-600 mt-1 max-w-2xl">
          You can either apply SRES&apos;s recommended defaults with a single
          click and continue to the estimation run, or dive into the
          detailed parameter workspace to tune density, statistics,
          composites, domains and variogram fits.
        </p>
      </header>

      {/* ─── QUICK-LINK: Recommended Defaults ────────────────────── */}
      <section className="rounded-xl border-2 border-brand-blue/30 bg-gradient-to-br from-brand-blue/5 to-white p-5"
                    data-testid="estimation-quick-defaults-card">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-brand-blue flex items-center justify-center text-white">
            <Lightning size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-black text-[16px] text-slate-900">
              Skip to Recommended Defaults
            </h2>
            <p className="text-[12.5px] text-slate-600 mt-0.5">
              Compress this ~5-minute step into one click. SRES will
              apply the following preset and take you directly to Step 7.
            </p>
            <div className="table-wrapper">
              <table className="mt-3 text-[11.5px] border border-slate-200 rounded overflow-hidden w-full max-w-xl">
                <tbody className="[&>tr>td]:px-2 [&>tr>td]:py-1.5 [&>tr]:border-b [&>tr]:border-slate-200 [&>tr:last-child]:border-b-0">
                  {PRESET_ROWS.map(([k, v]) => (
                    <tr key={k}>
                      <td className="text-slate-500 w-1/2">{k}</td>
                      <td className="text-slate-900 font-mono">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={() => applyMut.mutate()}
                disabled={applyMut.isPending || applied}
                data-testid="btn-apply-recommended-defaults"
                className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[12px] gap-1.5 h-10">
                {applied
                  ? <><CheckCircle size={14} weight="fill" /> Defaults applied — Loading Step 7…</>
                  : (applyMut.isPending
                    ? <><Sparkle size={14} className="animate-pulse" /> Applying…</>
                    : <><PlayCircle size={14} weight="fill" /> Apply defaults &amp; Continue <ArrowRight size={14} /></>)}
              </Button>
              <span className="text-[10.5px] text-slate-500">
                All values remain editable — you can still tune them from Step 7 or the Configuration workspace.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ADVANCED: Current Configuration + link into workspace ── */}
      <section className="rounded-lg border border-slate-200 bg-white p-4"
                    data-testid="estimation-advanced-card">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700">
            <Gear size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-bold text-[14px] text-slate-900">
              Advanced configuration
            </h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              For custom variogram fits, per-domain search parameters,
              and manual QA/QC review, open the Configuration workspace.
              Every field maps to the same backend endpoints — nothing
              is duplicated.
            </p>
            {hasCurrent && (
              <div className="mt-3 text-[11px] text-slate-600 font-mono">
                Current: block <b>
                  {modelling.block_size_x?.value ?? "—"} × {modelling.block_size_y?.value ?? "—"} × {modelling.block_size_z?.value ?? "—"} m
                </b> · composite <b>{modelling.composite_length?.value ?? "—"} m</b> · density <b>{modelling.density_default?.value ?? "—"} t/m³</b>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm"
                          data-testid="btn-open-configuration"
                          onClick={() => navigate(`/app/projects/${pid}/setup/configuration`)}
                          className="text-[11.5px] gap-1">
                Open Configuration Workspace <ArrowRight size={11} />
              </Button>
              <Button variant="ghost" size="sm"
                          data-testid="btn-continue-without-defaults"
                          onClick={() => navigate(`/app/projects/${pid}/wizard/block_model`)}
                          className="text-[11.5px] text-slate-500 hover:text-brand-blue">
                Continue without applying defaults →
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
